import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { PAGE_CHROME_LIGHT } from '../constants/pageChrome';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { greetingFirstName } from './account/accountShared';
import { ACCOUNT_PRIVACY_URL, ACCOUNT_TERMS_URL } from './account/accountSettingsContent';
import { stashMemberInviteCode } from '../lib/memberInviteAttribution';
import {
  wasSignupConfirmationRedirect,
  getExpectedSignupConfirmUserId,
  clearSignupConfirmExpectedUser,
} from '../lib/authEmailConfirm';
import {
  wasPasswordRecoveryRedirect,
  clearPasswordRecoveryCapture,
} from '../lib/authPasswordRecovery';
import {
  getPasswordResetRedirectUrl,
  getPostConfirmDashboardUrl,
  getSignupConfirmRedirectUrl,
} from '../lib/authRedirect';
import { webmailUrlForEmail } from '../lib/authOpenEmail';
import { ALL_COUNTRIES, REFERRAL_OPTIONS } from './auth/authCountries';
import {
  AuthChrome,
  authTitleClass,
  authSubtitleClass,
  authLabelClass,
  authPrimaryBtnClass,
  authQuietBtnClass,
  authLinkClass,
  authQuietLinkClass,
  authInputClass,
} from './auth/AuthChrome';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PENDING_EMAIL_KEY = 'bravos.pendingSignupEmail';
const PENDING_ROLE_KEY = 'bravos.pendingSignupRole';

type UserRole = 'member' | 'trainer';
type AuthScreen =
  | 'login'
  | 'choose-role'
  | 'signup'
  | 'check-inbox'
  | 'forgot'
  | 'reset-sent'
  | 'set-password';

const passwordStrength = (pw: string) => ({
  length: pw.length >= 8,
  uppercase: /[A-Z]/.test(pw),
  number: /[0-9]/.test(pw),
});

function readPendingSignup(): { email: string; role: UserRole } | null {
  try {
    const email = localStorage.getItem(PENDING_EMAIL_KEY)?.trim() || '';
    if (!email) return null;
    const role = localStorage.getItem(PENDING_ROLE_KEY) === 'trainer' ? 'trainer' : 'member';
    return { email, role };
  } catch {
    return null;
  }
}

function writePendingSignup(email: string, role: UserRole) {
  try {
    localStorage.setItem(PENDING_EMAIL_KEY, email);
    localStorage.setItem(PENDING_ROLE_KEY, role);
  } catch { /* ignore */ }
}

function clearPendingSignup() {
  try {
    localStorage.removeItem(PENDING_EMAIL_KEY);
    localStorage.removeItem(PENDING_ROLE_KEY);
  } catch { /* ignore */ }
}

const InlineError = ({ message }: { message: string }) => (
  <p className="mt-1.5 flex items-start gap-1.5 text-xs text-[var(--error)]">
    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    {message}
  </p>
);

function OpenEmailButton({ email }: { email: string }) {
  const href = webmailUrlForEmail(email);
  if (!href) return null;
  return (
    <a href={href} target="_blank" rel="noreferrer" className={`${authPrimaryBtnClass} mt-[18px]`}>
      <Mail className="h-[18px] w-[18px]" />
      Open your email
    </a>
  );
}

export const Auth = () => {
  usePageStatusBar({ background: PAGE_CHROME_LIGHT });
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { signIn, signUp, user, profile, loading: authLoading } = useAuth();

  const [screen, setScreen] = useState<AuthScreen>(() => {
    if (wasPasswordRecoveryRedirect()) return 'set-password';
    const pending = readPendingSignup();
    if (pending) return 'check-inbox';
    const mode = new URLSearchParams(window.location.search).get('mode');
    const role = new URLSearchParams(window.location.search).get('role');
    const invite = new URLSearchParams(window.location.search).get('invite');
    if (mode === 'signup' || invite) {
      if (invite || role === 'member' || role === 'trainer') return 'signup';
      return 'choose-role';
    }
    return 'login';
  });
  const [userRole, setUserRole] = useState<UserRole>(() => {
    const invite = new URLSearchParams(window.location.search).get('invite');
    if (invite) return 'member';
    return new URLSearchParams(window.location.search).get('role') === 'trainer' ? 'trainer' : 'member';
  });
  const [isLoading, setIsLoading] = useState(false);
  const [signupEmail, setSignupEmail] = useState(() => readPendingSignup()?.email || '');
  const [loginPending, setLoginPending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');
  const [unverifiedPrompt, setUnverifiedPrompt] = useState(false);
  const [accountExistsNotice, setAccountExistsNotice] = useState(false);
  const [credentialError, setCredentialError] = useState(false);
  const [recoveryUpdateRequired, setRecoveryUpdateRequired] = useState(() => wasPasswordRecoveryRedirect());
  const recoveryPrimedRef = useRef(false);
  const [justConfirmed, setJustConfirmed] = useState(() => wasSignupConfirmationRedirect());
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '', password: '',
    country: '', referralSource: '', marketingOptIn: false,
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string; password?: string;
    firstName?: string; lastName?: string; country?: string;
  }>({});

  useEffect(() => {
    if (loginPending && profile?.id) {
      window.location.href = profile.role === 'trainer' ? '/trainer-dashboard' : '/member-dashboard';
    }
  }, [loginPending, profile]);

  useEffect(() => {
    if (user && profile && !authLoading && !justConfirmed && !loginPending && !recoveryUpdateRequired) {
      const t = setTimeout(() => {
        navigate(profile.role === 'trainer' ? '/trainer-dashboard' : '/member-dashboard');
      }, 500);
      return () => clearTimeout(t);
    }
  }, [user, profile, authLoading, navigate, justConfirmed, loginPending, recoveryUpdateRequired]);

  useEffect(() => {
    const mode = searchParams.get('mode');
    const invite = searchParams.get('invite');
    const roleParam = searchParams.get('role');
    if (invite) stashMemberInviteCode(invite);
    if (recoveryUpdateRequired || justConfirmed) return;
    if (screen === 'check-inbox' || screen === 'reset-sent' || screen === 'set-password' || screen === 'forgot') return;

    if (mode === 'signup' || invite) {
      if (invite) {
        setUserRole('member');
        setScreen('signup');
      } else if (roleParam === 'trainer' || roleParam === 'member') {
        setUserRole(roleParam);
        setScreen('signup');
      } else {
        setScreen('choose-role');
      }
    } else if (mode === 'login') {
      setScreen('login');
    }
  }, [searchParams, recoveryUpdateRequired, justConfirmed, screen]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (wasPasswordRecoveryRedirect()) {
      setRecoveryUpdateRequired(true);
      setScreen('set-password');
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange(event => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryUpdateRequired(true);
        setScreen('set-password');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!recoveryUpdateRequired) {
      recoveryPrimedRef.current = false;
      return;
    }
    if (recoveryPrimedRef.current) return;
    recoveryPrimedRef.current = true;
    setFormData(prev => ({ ...prev, password: '' }));
    setScreen('set-password');
    setError('');
    setFieldErrors({});
  }, [recoveryUpdateRequired]);

  const goLogin = (opts?: { keepEmail?: boolean }) => {
    setScreen('login');
    setSearchParams({ mode: 'login' }, { replace: true });
    setUnverifiedPrompt(false);
    setAccountExistsNotice(false);
    setCredentialError(false);
    setError('');
    setFieldErrors({});
    setShowPassword(false);
    if (!opts?.keepEmail) {
      setFormData(prev => ({ ...prev, password: '', firstName: '', lastName: '', country: '', referralSource: '' }));
    } else {
      setFormData(prev => ({ ...prev, password: '' }));
    }
  };

  const goChooseRole = () => {
    setScreen('choose-role');
    setSearchParams({ mode: 'signup' }, { replace: true });
    setError('');
    setFieldErrors({});
    setUnverifiedPrompt(false);
    setAccountExistsNotice(false);
  };

  const goSignup = (role: UserRole, preserveFields = true) => {
    setUserRole(role);
    setScreen('signup');
    const next: Record<string, string> = { mode: 'signup', role };
    const invite = searchParams.get('invite');
    if (invite && role === 'member') next.invite = invite;
    setSearchParams(next, { replace: true });
    if (!preserveFields) {
      setFormData({
        firstName: '', lastName: '', email: '', password: '',
        country: '', referralSource: '', marketingOptIn: false,
      });
    }
    setError('');
    setFieldErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'email') {
      setAccountExistsNotice(false);
      setUnverifiedPrompt(false);
      setCredentialError(false);
    }
    if (error) setError('');
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const validateEmailField = () => {
    if (!formData.email.trim()) return;
    if (!EMAIL_RE.test(formData.email)) {
      setFieldErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    }
  };

  const runSignupResend = async (email: string): Promise<boolean> => {
    setResendError('');
    try {
      const { error: resendErr } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: { emailRedirectTo: getSignupConfirmRedirectUrl() },
      });
      if (resendErr) {
        if (resendErr.status === 429) {
          setResendError('Too many requests — please wait a minute before trying again.');
          setResendCooldown(60);
        } else {
          setResendError('Could not resend email. Please try again later.');
        }
        return false;
      }
      setResendSuccess(true);
      setResendCooldown(60);
      setTimeout(() => setResendSuccess(false), 4000);
      return true;
    } catch {
      setResendError('Could not resend email. Please try again later.');
      return false;
    }
  };

  const validateForm = (): boolean => {
    setError('');
    const errs: typeof fieldErrors = {};
    if (!formData.email.trim()) errs.email = 'Email is required';
    else if (!EMAIL_RE.test(formData.email)) errs.email = 'Please enter a valid email address';

    if (screen !== 'forgot') {
      if (!formData.password.trim()) errs.password = 'Password is required';
      else if (screen === 'signup') {
        const s = passwordStrength(formData.password);
        if (!s.length || !s.uppercase || !s.number) {
          errs.password = 'Must be 8+ characters with one uppercase letter and one number';
        }
      }
    }

    if (screen === 'signup') {
      if (!formData.firstName.trim()) errs.firstName = 'First name is required';
      if (!formData.lastName.trim()) errs.lastName = 'Last name is required';
      if (userRole === 'trainer' && !formData.country.trim()) errs.country = 'Country is required';
    }

    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleForgotPasswordSend = async () => {
    setError('');
    setFieldErrors(prev => ({ ...prev, email: undefined }));
    if (!formData.email.trim()) {
      setFieldErrors(prev => ({ ...prev, email: 'Email is required' }));
      return;
    }
    if (!EMAIL_RE.test(formData.email)) {
      setFieldErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      return;
    }
    setIsLoading(true);
    try {
      const { error: resetErr } = await supabase.auth.resetPasswordForEmail(formData.email.trim(), {
        redirectTo: getPasswordResetRedirectUrl(),
      });
      if (resetErr) {
        const m = resetErr.message?.toLowerCase() ?? '';
        if (m.includes('rate') || resetErr.status === 429) {
          setError('Too many requests. Please wait a few minutes and try again.');
        } else {
          setError(resetErr.message || 'Could not send reset email. Please try again.');
        }
        return;
      }
      setScreen('reset-sent');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRecoveryPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const s = passwordStrength(formData.password);
    if (!formData.password.trim()) {
      setFieldErrors({ password: 'Password is required' });
      return;
    }
    if (!s.length || !s.uppercase || !s.number) {
      setFieldErrors({ password: 'Must be 8+ characters with one uppercase letter and one number' });
      return;
    }
    setFieldErrors({});
    if (!user) {
      setError('This reset link is invalid or has expired. Request a new one from sign in.');
      return;
    }
    setIsLoading(true);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password: formData.password });
      if (updErr) {
        const msg = updErr.message || 'Could not update password.';
        setError(
          msg.toLowerCase().includes('session missing')
            ? 'This reset link is invalid or has expired. Request a new one from sign in.'
            : msg
        );
        return;
      }
      setRecoveryUpdateRequired(false);
      recoveryPrimedRef.current = false;
      clearPasswordRecoveryCapture();
      if (window.location.hash || window.location.search.includes('type=recovery')) {
        window.history.replaceState(null, '', window.location.pathname);
      }
      const role = profile?.role;
      if (role) {
        navigate(role === 'trainer' ? '/trainer-dashboard' : '/member-dashboard', { replace: true });
      } else {
        setLoginPending(true);
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (screen === 'forgot') {
      await handleForgotPasswordSend();
      return;
    }
    if (screen === 'signup' && unverifiedPrompt) return;
    if (!validateForm()) return;
    setIsLoading(true);
    setError('');
    setCredentialError(false);

    try {
      if (screen === 'login') {
        const { user: authUser, error: authError } = await signIn(formData.email, formData.password);
        if (authError) {
          const msg = authError.message?.toLowerCase() ?? '';
          if (msg.includes('email not confirmed')) {
            setUnverifiedPrompt(true);
            setError('Please confirm your email before signing in. Check your inbox for the confirmation link.');
          } else if (msg.includes('invalid login') || msg.includes('invalid credentials')) {
            setCredentialError(true);
            setError("That email and password don't match. Check your details, or reset your password.");
            setFieldErrors(prev => ({ ...prev, password: ' ' }));
          } else if (msg.includes('rate limit') || msg.includes('429')) {
            setError('Too many attempts. Please wait a few minutes and try again.');
          } else {
            setError(authError.message);
          }
          return;
        }
        if (authUser) setLoginPending(true);
      } else {
        setUnverifiedPrompt(false);
        const submittedEmail = formData.email.trim();
        const { data: statusRaw, error: classifyErr } = await supabase.rpc('signup_email_status', {
          check_email: submittedEmail,
        });
        if (classifyErr) {
          setError('Could not verify this email. Please try again.');
          return;
        }
        const status = typeof statusRaw === 'string' ? statusRaw.trim().toLowerCase() : '';
        if (status === 'verified') {
          setFormData(prev => ({ ...prev, email: submittedEmail, password: '' }));
          goLogin({ keepEmail: true });
          setAccountExistsNotice(true);
          setFieldErrors({
            email: 'You already have an account with this email.',
          });
          return;
        }
        if (status === 'unverified') {
          setSignupEmail(submittedEmail);
          writePendingSignup(submittedEmail, userRole);
          setUnverifiedPrompt(true);
          setScreen('check-inbox');
          return;
        }
        if (status !== 'available') {
          setError('Could not verify this email. Please try again.');
          return;
        }

        const fullName = [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(' ');
        const userData = {
          name: fullName,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          role: userRole,
          country: userRole === 'trainer' ? formData.country : undefined,
          referral_source: userRole === 'trainer' ? formData.referralSource || undefined : undefined,
          marketing_notifications_enabled: Boolean(formData.marketingOptIn),
        };

        const { user: authUser, error: authError } = await signUp(formData.email, formData.password, userData);
        if (authError) {
          const msg = authError.message?.toLowerCase() ?? '';
          if (msg.includes('rate limit') || msg.includes('429')) {
            setError('Too many sign-up attempts. Please wait a few minutes and try again.');
          } else {
            setError(authError.message);
          }
          return;
        }
        if (authUser) {
          if ((authUser as any).identities?.length === 0) {
            setFormData(prev => ({ ...prev, email: submittedEmail, password: '' }));
            goLogin({ keepEmail: true });
            setAccountExistsNotice(true);
            return;
          }
          setSignupEmail(submittedEmail);
          writePendingSignup(submittedEmail, userRole);
          setScreen('check-inbox');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const pw = passwordStrength(formData.password);
  const signupValid =
    !!formData.firstName.trim() &&
    !!formData.lastName.trim() &&
    EMAIL_RE.test(formData.email) &&
    pw.length && pw.uppercase && pw.number &&
    (userRole !== 'trainer' || !!formData.country.trim());

  const passwordField = (
    name: string,
    placeholder: string,
    autoComplete: string,
    errorMsg?: string,
  ) => (
    <div className="mt-[18px]">
      <label htmlFor={name} className={authLabelClass}>Password</label>
      <div className="relative">
        <input
          id={name}
          name={name}
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange}
          className={`${authInputClass(!!errorMsg)} pr-12`}
          placeholder={placeholder}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          onClick={() => setShowPassword(v => !v)}
          className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-[var(--text-secondary)]"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
        </button>
      </div>
      {errorMsg && errorMsg.trim() ? <InlineError message={errorMsg} /> : null}
    </div>
  );

  const strengthMeter = formData.password.length > 0 ? (
    <div className="mt-2.5">
      <div className="flex gap-1.5">
        {[0, 1, 2].map(i => {
          const met = [pw.length, pw.uppercase, pw.number].filter(Boolean).length;
          const on = met >= i + 1;
          const color = met >= 3 ? 'bg-[var(--success)]' : met === 2 ? 'bg-[var(--action-primary-pressed)]' : 'bg-[#f59e0b]';
          return <span key={i} className={`h-1 flex-1 rounded-sm ${on ? color : 'bg-[#e4e4e2]'}`} />;
        })}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-3.5 gap-y-2 text-xs">
        {[
          [pw.length, '8+ characters'],
          [pw.uppercase, 'Uppercase letter'],
          [pw.number, 'Number'],
        ].map(([met, label]) => (
          <span key={String(label)} className={`flex items-center gap-1.5 ${met ? 'text-ink' : 'text-ink/45'}`}>
            <CheckCircle className="h-3.5 w-3.5" />
            {label}
          </span>
        ))}
      </div>
    </div>
  ) : (
    <p className="mt-1.5 text-xs leading-snug text-[var(--text-secondary)]">Use 8+ characters with an uppercase letter and a number.</p>
  );

  if (recoveryUpdateRequired) {
    if (!user) {
      if (authLoading) return <LoadingSpinner fullScreen />;
      return (
        <AuthChrome>
          <h1 className={authTitleClass}>Reset link expired</h1>
          <p className={authSubtitleClass}>This password reset link is invalid or has expired. Request a new one from the sign-in page.</p>
          {error && (
            <div className="mt-4 flex gap-2.5 rounded-xl border border-[#fecaca] bg-[#fef2f2] p-3.5 text-sm text-[#b91c1c]">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </div>
          )}
          <button
            type="button"
            className={`${authPrimaryBtnClass} mt-[22px]`}
            onClick={() => {
              setRecoveryUpdateRequired(false);
              recoveryPrimedRef.current = false;
              clearPasswordRecoveryCapture();
              window.history.replaceState(null, '', '/auth?mode=login');
              goLogin();
            }}
          >
            Back to sign in
          </button>
        </AuthChrome>
      );
    }

    return (
      <AuthChrome>
        <h1 className={authTitleClass}>Set a new<br />password</h1>
        <p className={authSubtitleClass}>Choose a strong password for your account.</p>
        {error && (
          <div className="mt-4 flex gap-2.5 rounded-xl border border-[#fecaca] bg-[#fef2f2] p-3.5 text-sm text-[#b91c1c]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </div>
        )}
        <form onSubmit={handleRecoveryPasswordSubmit}>
          {passwordField('password', 'At least 8 characters', 'new-password', fieldErrors.password)}
          {strengthMeter}
          <button type="submit" disabled={isLoading || !pw.length || !pw.uppercase || !pw.number} className={`${authPrimaryBtnClass} mt-[22px]`}>
            {isLoading ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </AuthChrome>
    );
  }

  if (user && !profile && authLoading) return <LoadingSpinner fullScreen />;

  if (justConfirmed) {
    const expectedConfirmUserId = getExpectedSignupConfirmUserId();
    if (user && profile && profile.id === user.id && (!expectedConfirmUserId || user.id === expectedConfirmUserId)) {
      const displayName = greetingFirstName(profile, user) || undefined;
      clearPendingSignup();
      return (
        <AuthChrome>
          <h1 className={authTitleClass}>Your email is confirmed</h1>
          <p className={authSubtitleClass}>
            {displayName ? `Welcome to Bravos, ${displayName}. Your account is now active.` : 'Welcome to Bravos. Your account is now active.'}
          </p>
          <a
            href={getPostConfirmDashboardUrl(profile.role)}
            className={`${authPrimaryBtnClass} mt-7 no-underline`}
            onClick={() => clearSignupConfirmExpectedUser()}
          >
            Go to my dashboard
          </a>
        </AuthChrome>
      );
    }
    if (!authLoading && (!user || (expectedConfirmUserId && user.id !== expectedConfirmUserId))) {
      return (
        <AuthChrome>
          <h1 className={authTitleClass}>Your email is confirmed</h1>
          <p className={authSubtitleClass}>Welcome to Bravos. Your account is now active. Sign in to continue.</p>
          <button
            type="button"
            className={`${authPrimaryBtnClass} mt-7`}
            onClick={() => {
              clearSignupConfirmExpectedUser();
              setJustConfirmed(false);
              goLogin();
            }}
          >
            Continue to sign in
          </button>
        </AuthChrome>
      );
    }
    return <LoadingSpinner fullScreen />;
  }

  if (screen === 'check-inbox' && signupEmail) {
    return (
      <AuthChrome>
        <div className="pt-10 text-center">
          <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[var(--success-tint)]">
            <Mail className="h-8 w-8 text-[var(--ink)]" />
          </div>
          <h1 className={`${authTitleClass} mt-[26px]`}>Check your inbox</h1>
          <p className={authSubtitleClass}>We sent a confirmation link to</p>
          <p className="mt-1 text-[15px] font-semibold break-all">{signupEmail}</p>
          <p className={`${authSubtitleClass} mt-3.5`}>
            Open the link to activate your account. If it isn’t there in a minute, check spam.
          </p>
          {resendSuccess && (
            <div className="mt-6 flex gap-2.5 rounded-xl border border-[rgba(179, 231, 131,0.55)] bg-[rgba(179, 231, 131,0.28)] p-3.5 text-left text-sm text-[var(--ink)]">
              <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              Confirmation email sent again.
            </div>
          )}
          {resendError && (
            <div className="mt-6 flex gap-2.5 rounded-xl border border-[#fecaca] bg-[#fef2f2] p-3.5 text-left text-sm text-[#b91c1c]">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {resendError}
            </div>
          )}
          <OpenEmailButton email={signupEmail} />
          <button
            type="button"
            disabled={resendCooldown > 0}
            onClick={() => runSignupResend(signupEmail)}
            className={`${authQuietBtnClass} mt-2.5`}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend confirmation email'}
          </button>
          <div className="mt-[18px]">
            <button
              type="button"
              className={authQuietLinkClass}
              onClick={() => {
                clearPendingSignup();
                setSignupEmail('');
                setFormData(prev => ({ ...prev, email: '' }));
                goSignup(userRole, true);
              }}
            >
              Use a different email
            </button>
          </div>
          <div className="mt-3">
            <button
              type="button"
              className={authQuietLinkClass}
              onClick={() => {
                clearPendingSignup();
                setSignupEmail('');
                setFormData({
                  firstName: '', lastName: '', email: '', password: '',
                  country: '', referralSource: '', marketingOptIn: false,
                });
                goLogin();
              }}
            >
              Sign in with another account
            </button>
          </div>
          <div className="mt-3">
            <button
              type="button"
              className={authQuietLinkClass}
              onClick={() => {
                clearPendingSignup();
                setSignupEmail('');
                setFormData({
                  firstName: '', lastName: '', email: '', password: '',
                  country: '', referralSource: '', marketingOptIn: false,
                });
                goChooseRole();
              }}
            >
              Create an account
            </button>
          </div>
        </div>
      </AuthChrome>
    );
  }

  if (screen === 'reset-sent') {
    return (
      <AuthChrome>
        <div className="pt-10 text-center">
          <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[var(--success-tint)]">
            <Mail className="h-8 w-8 text-[var(--ink)]" />
          </div>
          <h1 className={`${authTitleClass} mt-[26px]`}>Check your inbox</h1>
          <p className={authSubtitleClass}>If an account exists for</p>
          <p className="mt-1 text-[15px] font-semibold break-all">{formData.email.trim()}</p>
          <p className={`${authSubtitleClass} mt-2`}>we’ve sent a link to reset your password. The link expires in 60 minutes.</p>
          <OpenEmailButton email={formData.email} />
          <button
            type="button"
            disabled={isLoading || resendCooldown > 0}
            onClick={handleForgotPasswordSend}
            className={`${authQuietBtnClass} mt-2.5`}
          >
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : isLoading ? 'Sending…' : 'Resend reset link'}
          </button>
          <div className="mt-[18px]">
            <button type="button" className={authQuietLinkClass} onClick={() => goLogin({ keepEmail: true })}>
              Back to sign in
            </button>
          </div>
        </div>
      </AuthChrome>
    );
  }

  if (screen === 'choose-role') {
    return (
      <AuthChrome wide>
        <h1 className={authTitleClass}>How will you use Bravos?</h1>
        <p className={authSubtitleClass}>You can always sign in later — this just sets up the right account.</p>
        <div className="mt-7 grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => goSignup('member')}
            className="flex min-h-[160px] flex-col rounded-2xl border-[1.5px] border-[rgba(229,229,229,0.8)] bg-white p-5 text-left transition hover:border-[var(--ink)] sm:min-h-[228px]"
            aria-label="Member"
          >
            <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--success-tint)] text-[var(--ink)]">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="12" cy="5" r="2.2"/><path d="M8 21v-3.2a4 4 0 0 1 2.4-3.7L12 13l1.6 1.1A4 4 0 0 1 16 17.8V21"/></svg>
            </span>
            <span className="font-pageTitle text-xl font-bold tracking-[-0.03em]">Member</span>
            <span className="mt-2 flex-1 text-[13px] leading-snug text-[var(--text-secondary)]">Follow a plan, train on your own or with a coach, and keep everything in one place.</span>
            <span className="mt-4 text-[13px] font-semibold">Continue →</span>
          </button>
          <button
            type="button"
            onClick={() => goSignup('trainer')}
            className="flex min-h-[160px] flex-col rounded-2xl border-[1.5px] border-[rgba(229,229,229,0.8)] bg-white p-5 text-left transition hover:border-[var(--ink)] sm:min-h-[228px]"
            aria-label="Personal trainer"
          >
            <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-[var(--success-tint)] text-[var(--ink)]">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2"/><circle cx="9.5" cy="7" r="3.2"/><path d="M20 8v6M17 11h6"/></svg>
            </span>
            <span className="font-pageTitle text-xl font-bold tracking-[-0.03em]">Personal trainer</span>
            <span className="mt-2 flex-1 text-[13px] leading-snug text-[var(--text-secondary)]">Invite clients, build programmes, and run your coaching business from the web dashboard.</span>
            <span className="mt-4 text-[13px] font-semibold">Continue →</span>
          </button>
        </div>
        <p className="mt-7 text-center text-sm text-[var(--text-secondary)]">
          Already have an account?{' '}
          <button type="button" className={authLinkClass} onClick={() => goLogin()}>Sign in</button>
        </p>
      </AuthChrome>
    );
  }

  if (screen === 'forgot') {
    return (
      <AuthChrome>
        <h1 className={authTitleClass}>Reset your<br />password</h1>
        <p className={authSubtitleClass}>Enter the email on your account and we’ll send a link to set a new password.</p>
        {error && (
          <div className="mt-4 flex gap-2.5 rounded-xl border border-[#fecaca] bg-[#fef2f2] p-3.5 text-sm text-[#b91c1c]">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="mt-2">
          <div className="mt-[26px]">
            <label htmlFor="email" className={authLabelClass}>Email</label>
            <input
              id="email" name="email" type="email"
              value={formData.email} onChange={handleInputChange} onBlur={validateEmailField}
              className={authInputClass(!!fieldErrors.email)}
              placeholder="you@example.com" autoComplete="email"
            />
            {fieldErrors.email && <InlineError message={fieldErrors.email} />}
          </div>
          <button type="submit" disabled={isLoading} className={`${authPrimaryBtnClass} mt-[22px]`}>
            {isLoading ? 'Sending…' : 'Send reset link'}
          </button>
          <p className="mt-[22px] text-center">
            <button type="button" className={authQuietLinkClass} onClick={() => goLogin({ keepEmail: true })}>
              Back to sign in
            </button>
          </p>
        </form>
      </AuthChrome>
    );
  }

  const isSignup = screen === 'signup';

  return (
    <AuthChrome>
      <h1 className={authTitleClass}>
        {isSignup
          ? (userRole === 'trainer' ? 'Create your trainer account' : <>Create your<br />account</>)
          : 'Welcome back'}
      </h1>
      <p className={authSubtitleClass}>
        {isSignup
          ? (userRole === 'trainer'
            ? 'Connect with members and grow your training business.'
            : 'Start training with a plan that fits you.')
          : 'Pick up where you left off.'}
      </p>

      {isSignup && (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-2xl border border-[rgba(179, 231, 131,0.55)] bg-[rgba(179, 231, 131,0.28)] px-3.5 py-3">
          <p className="text-[13px] leading-snug text-[var(--ink)]">
            Signing up as a <span className="font-semibold text-[var(--ink)]">{userRole === 'trainer' ? 'personal trainer' : 'member'}</span>
          </p>
          <button type="button" className="shrink-0 text-[13px] font-semibold text-[var(--ink)] underline underline-offset-[3px]" onClick={goChooseRole}>
            Switch
          </button>
        </div>
      )}

      {error && (
        <div className="mt-5 flex gap-2.5 rounded-xl border border-[#fecaca] bg-[#fef2f2] p-3.5 text-sm leading-snug text-[#b91c1c]">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {error}
            {credentialError && (
              <>
                {' '}
                <button type="button" className="font-semibold underline underline-offset-2" onClick={() => setScreen('forgot')}>
                  reset your password
                </button>
              </>
            )}
          </span>
        </div>
      )}

      {unverifiedPrompt && screen === 'login' && (
        <button type="button" className={`${authLinkClass} mt-3 text-sm`} onClick={() => runSignupResend(formData.email.trim())}>
          {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend confirmation email'}
        </button>
      )}

      {accountExistsNotice && screen === 'login' && (
        <p className="mt-4 rounded-xl border border-line bg-subtle px-3 py-3 text-sm">
          An account with this email already exists. Sign in to continue or reset your password if you forgot it.
        </p>
      )}

      <form onSubmit={handleSubmit}>
        {isSignup && (
          <div className="mt-6 grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="firstName" className={authLabelClass}>First name</label>
              <input id="firstName" name="firstName" type="text" value={formData.firstName} onChange={handleInputChange}
                className={authInputClass(!!fieldErrors.firstName)} placeholder="Alex" autoComplete="given-name" />
              {fieldErrors.firstName && <InlineError message={fieldErrors.firstName} />}
            </div>
            <div>
              <label htmlFor="lastName" className={authLabelClass}>Last name</label>
              <input id="lastName" name="lastName" type="text" value={formData.lastName} onChange={handleInputChange}
                className={authInputClass(!!fieldErrors.lastName)} placeholder="Johnson" autoComplete="family-name" />
              {fieldErrors.lastName && <InlineError message={fieldErrors.lastName} />}
            </div>
          </div>
        )}

        <div className={isSignup ? 'mt-[18px]' : 'mt-6'}>
          <label htmlFor="email" className={authLabelClass}>Email</label>
          <input
            id="email" name="email" type="email"
            value={formData.email} onChange={handleInputChange} onBlur={validateEmailField}
            className={authInputClass(!!fieldErrors.email)}
            placeholder="you@example.com" autoComplete="email"
          />
          {fieldErrors.email && (
            <InlineError
              message={
                fieldErrors.email.includes('already')
                  ? 'You already have an account with this email. Sign in instead.'
                  : fieldErrors.email
              }
            />
          )}
        </div>

        {passwordField(
          'password',
          isSignup ? 'At least 8 characters' : 'Enter your password',
          isSignup ? 'new-password' : 'current-password',
          fieldErrors.password,
        )}
        {isSignup && strengthMeter}
        {!isSignup && (
          <div className="mt-2.5 text-right">
            <button type="button" className={`${authQuietLinkClass} text-[13px]`} onClick={() => setScreen('forgot')}>
              Forgot password?
            </button>
          </div>
        )}

        {isSignup && userRole === 'trainer' && (
          <>
            <div className="mt-[18px]">
              <label htmlFor="country" className={authLabelClass}>Country</label>
              <select
                id="country" name="country" value={formData.country} onChange={handleInputChange}
                className={authInputClass(!!fieldErrors.country)}
              >
                <option value="">Select your country</option>
                {ALL_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {fieldErrors.country && <InlineError message={fieldErrors.country} />}
            </div>
            <div className="mt-[18px]">
              <label htmlFor="referralSource" className={authLabelClass}>
                How did you hear about us? <span className="font-normal">(optional)</span>
              </label>
              <select
                id="referralSource" name="referralSource" value={formData.referralSource} onChange={handleInputChange}
                className={authInputClass()}
              >
                <option value="">Select an option</option>
                {REFERRAL_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </>
        )}

        {isSignup && (
          <div className="mt-[22px] flex items-start gap-3.5 rounded-2xl bg-subtle px-4 py-3.5">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">Tips and offers</p>
              <p className="mt-0.5 text-xs leading-snug text-[var(--text-secondary)]">
                {userRole === 'trainer'
                  ? 'Occasional emails about product updates. No spam.'
                  : 'Occasional emails and push about new programmes. No spam.'}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={formData.marketingOptIn}
              onClick={() => setFormData(prev => ({ ...prev, marketingOptIn: !prev.marketingOptIn }))}
              className={`relative h-[31px] w-[51px] shrink-0 rounded-full transition-colors ${formData.marketingOptIn ? 'bg-[var(--success)]' : 'bg-[#dcdcda]'}`}
            >
              <span className={`absolute top-0.5 h-[27px] w-[27px] rounded-full bg-white shadow ${formData.marketingOptIn ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || (isSignup && !signupValid)}
          className={`${authPrimaryBtnClass} mt-[22px]`}
        >
          {isLoading
            ? (isSignup ? 'Creating account…' : 'Signing in…')
            : isSignup
              ? (userRole === 'trainer' ? 'Create trainer account' : 'Create account')
              : 'Sign in'}
        </button>

        {isSignup && (
          <p className="mt-4 text-center text-xs leading-relaxed text-[var(--text-secondary)]">
            By creating an account you agree to our{' '}
            <a href={ACCOUNT_TERMS_URL} target="_blank" rel="noreferrer" className="text-[var(--text-secondary)] underline underline-offset-2">Terms of Service</a>
            {' '}and{' '}
            <a href={ACCOUNT_PRIVACY_URL} target="_blank" rel="noreferrer" className="text-[var(--text-secondary)] underline underline-offset-2">Privacy Policy</a>.
          </p>
        )}
      </form>

      <p className="mt-[26px] pb-4 text-center text-sm text-[var(--text-secondary)]">
        {isSignup ? (
          <>Already have an account? <button type="button" className={authLinkClass} onClick={() => goLogin()}>Sign in</button></>
        ) : (
          <>New to Bravos? <button type="button" className={authLinkClass} onClick={goChooseRole}>Create an account</button></>
        )}
      </p>
    </AuthChrome>
  );
};

export default Auth;
